import React from "react";
import {useTheme} from "@mui/material/styles";
import {CircularProgress} from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import Switch from "@mui/material/Switch";
import Select from "@mui/material/Select";
import InfoIcon from "@mui/icons-material/Info";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import DatasetColumnEditor from "./DatasetColumnEditor";


export default function DatasetConfigurator(props) {
    const theme = useTheme();
    // init internationalization
    const {t} = useTranslation();

    const [CSVEncoding] = React.useState("UTF-8");
    const [uploadClicked, setUploadClicked] = React.useState(false);

    const handleDatasetNameChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        props.setDatasetName(event.target.value);
    };

    const handleButtonClick = (event) => {
        // simple method to set variable to true after button click to display Spinner
        setUploadClicked(true);
        // then call actual next handling method
        props.handleNext(event);
    }

    return (
        <Grid
            id="dataset-configurator-container"
            container
            spacing={2}
            sx={{padding: theme.spacing(8, 1)}}
        >
            <Grid
                item
                xs={5}
                sx={{
                    "& .MuiSelect-select": {minWidth: 100},
                    "& .MuiFormControlLabel-root": {width: "auto"},
                }}
            >
                <FormControl>
                    <TextField
                        id="filled-basic"
                        label={t("datasetConfigurator.DatasetNameFillable")}
                        defaultValue={props.datasetName}
                        error={Boolean(props.errors.datasetName)}
                        onChange={handleDatasetNameChange}
                        variant="filled"
                    />
                </FormControl>
                <Tooltip title={t("datasetConfigurator.HintDatasetName")}>
                    <IconButton>
                        <InfoIcon/>
                    </IconButton>
                </Tooltip>
                <br/>
                <Typography variant="h6">
                    <br/>
                    {t("datasetConfigurator.FileSettingsTitle")}
                </Typography>
                <br/>
                <FormControl variant="filled" disabled>
                    <InputLabel>
                        {t("datasetConfigurator.CSVEncodingDropdown")}
                    </InputLabel>
                    <Select value={CSVEncoding}>
                        <MenuItem value="UTF-8">UTF-8</MenuItem>
                        <MenuItem value="ISO 8859-1 ">ISO 8859-1</MenuItem>
                    </Select>
                </FormControl>
                <Tooltip title={t("datasetConfigurator.HintEncoding")}>
                    <IconButton>
                        <InfoIcon/>
                    </IconButton>
                </Tooltip>
                <FormControl variant="filled" disabled>
                    <InputLabel>
                        {t("datasetConfigurator.CSVDelimiterDropdown")}
                    </InputLabel>
                    <Select value={props.CSVMeta.delimiter}>
                        <MenuItem value=",">,</MenuItem>
                        <MenuItem value=";">;</MenuItem>
                    </Select>
                </FormControl>
                <Tooltip title={t("datasetConfigurator.HintDelimiter")}>
                    <IconButton>
                        <InfoIcon/>
                    </IconButton>
                </Tooltip>
                <Typography variant="h6">
                    <br/>
                    {t("datasetConfigurator.DatasetSettingsTitle")}
                    <Tooltip title={t("datasetConfigurator.HintDatasetSettings")}>
                        <IconButton>
                            <InfoIcon/>
                        </IconButton>
                    </Tooltip>
                </Typography>
                <br/>
                <FormGroup>
                    <FormControlLabel
                        control={<Switch disabled/>}
                        label={t("datasetConfigurator.UseCustomVal")}
                    />
                    <FormControlLabel
                        disabled
                        control={<Switch disabled/>}
                        label={t("datasetConfigurator.UseTest")}
                    />
                </FormGroup>
                <br/>
            </Grid>
            <Grid item xs={6}>
                <Typography variant="h6">
                    {t("datasetConfigurator.TablePreviewTitle")}
                </Typography>
                <br/>
                <TableContainer component={Paper} sx={{maxWidth: "50vw"}}>
                    <Table size="small">
                        <caption>
                            {t("datasetConfigurator.TableDescription1")} ({" "}
                            {props.CSVMeta.fields.length}{" "}
                            {t("datasetConfigurator.TableDescription2")}x{" "}
                            {props.parsedCSV.length}{" "}
                            {t("datasetConfigurator.TableDescription3")})
                        </caption>
                        <TableHead>
                            <TableRow>
                                {Object.keys(props.parsedCSV[0]).map((column, index) => (
                                    <TableCell key={`col-${index}`}>{column}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {/* display first 5 rows */}
                            {props.parsedCSV.slice(0, 5).map((row, indexRow) => (
                                <TableRow key={indexRow}>
                                    {Object.keys(props.parsedCSV[0]).map((column, indexCol) => (
                                        <TableCell key={`row-${indexRow}-${indexCol}`}>
                                            {row[column]}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grid>

            {props.showEditor && <DatasetColumnEditor columnNamesTooLong={props.columnNamesTooLong}
                                                      handleTablePreviewRefresh={props.handleTablePreviewRefresh}/>}
            {!uploadClicked ? (
                <Box sx={{display: "flex", flexDirection: "row", pt: 2, width: "100%"}}>
                    <Button
                        color="inherit"
                        disabled={props.activeStep === 0}
                        onClick={props.handleBack}
                        sx={{mr: 1}}
                    >
                        {t("datasetCreator.BackButton")}
                    </Button>
                    <Box sx={{flex: "1 1 auto"}}/>
                    <Button onClick={handleButtonClick}>
                        {t("datasetCreator.StartCreationButton")}
                    </Button>
                </Box>
            ) : (
                 <Box sx={{display: "flex", flexDirection: "row", pt: 2, width: "100%"}}>
                    <CircularProgress/>
                    <p>&nbsp;{t("datasetCreator.DatasetCreationInProgress")}</p>
                </Box>
            )}
        </Grid>
    );
}
